/**
 * SatelliteGroundPlane — V14.68.6 Sprint 32
 *
 * Utility: adds a Three.js PlaneGeometry textured with the project's IGN
 * satellite tile onto an existing scene, centered on world origin (0,0,0)
 * at Y = groundY. Returns a cleanup function that removes the mesh and
 * disposes GPU resources.
 *
 * MVP: assumes the 3D model is centered on world origin and North-up. Perfect
 * GPS-aware alignment is deferred (needs georef in plan_source_editor).
 *
 * Called from viewer `onCreate` callbacks — Three.js scene lifecycle is
 * managed by the caller.
 */
import * as THREE from 'three';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Add a satellite ground plane to the scene. Returns an async-friendly cleanup.
 *
 * @param {object} args
 * @param {THREE.Scene} args.scene
 * @param {string} args.projectId
 * @param {number} args.radiusM  Half-size of the plane in meters
 * @param {number} [args.groundY=0]
 * @param {number} [args.opacity=0.95]
 * @returns {() => void} cleanup
 */
export function addSatelliteGroundPlane({ scene, projectId, radiusM, groundY = 0, opacity = 0.95, rotationDeg = 0, offsetXM = 0, offsetZM = 0 }) {
  if (!scene || !projectId || !(radiusM > 0)) return () => {};

  let cancelled = false;
  const disposers = [];

  (async () => {
    try {
      // Fetch the satellite tile metadata (verifies project has coords)
      const metaResp = await fetch(
        `${API_BASE}/api/projects/${projectId}/cadastre-static-map?width=1024&height=1024&maptype=satellite`,
        { credentials: 'include' },
      );
      if (!metaResp.ok || cancelled) return;
      const meta = await metaResp.json();
      if (!meta?.image_endpoint || cancelled) return;

      // Fetch the PNG bytes (auth-cookie proxied)
      const imgResp = await fetch(`${API_BASE}${meta.image_endpoint}`, { credentials: 'include' });
      if (!imgResp.ok || cancelled) return;
      const blob = await imgResp.blob();
      const objUrl = URL.createObjectURL(blob);
      disposers.push(() => URL.revokeObjectURL(objUrl));

      // Load into a three.js texture
      const loader = new THREE.TextureLoader();
      const tex = await new Promise((resolve, reject) => {
        loader.load(objUrl, resolve, undefined, reject);
      });
      if (cancelled) { tex.dispose(); return; }
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      disposers.push(() => tex.dispose());

      // Sprint 32.5 — Use the actual ground extent from Nominatim/Google Static Maps
      // meters_per_px so the plane is metrically accurate. Fallback to radiusM*2
      // if the endpoint doesn't return this field.
      const [imgW, imgH] = meta.image_size || [1024, 1024];
      const mpp = Number.isFinite(meta.meters_per_px) && meta.meters_per_px > 0 ? meta.meters_per_px : null;
      const trueW = mpp ? mpp * imgW : radiusM * 2;
      const trueH = mpp ? mpp * imgH : radiusM * 2;
      const geom = new THREE.PlaneGeometry(trueW, trueH, 1, 1);
      disposers.push(() => geom.dispose());
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });
      disposers.push(() => mat.dispose());
      const mesh = new THREE.Mesh(geom, mat);
      // Rotate flat on XZ plane, then apply user-defined north-alignment rotation
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = (rotationDeg || 0) * Math.PI / 180;
      mesh.position.set(offsetXM || 0, groundY, offsetZM || 0);
      mesh.renderOrder = -1;
      mesh.name = 'satellite-ground-plane';
      scene.add(mesh);
      disposers.push(() => scene.remove(mesh));
    } catch {
      // Silent — no satellite is a graceful fallback (project sans GPS)
    }
  })();

  return () => {
    cancelled = true;
    while (disposers.length) {
      try { disposers.pop()(); } catch { /* noop */ }
    }
  };
}
